// PRD.323 — the inside of one placed Render-mode mapping.
//
// Map Fields, Preview and the final TemplateOverlayView all render through this
// component, so a label position or alignment an admin sets while positioning
// is exactly what the patient's printed form shows. Before this, each surface
// built its own `justifyMap` inline.
//
// GEOMETRY IS THE CALLER'S. This fills the rectangle it is given and never sets
// position or size, so drag/resize semantics and the stored percentage
// coordinates are untouched. The label lives INSIDE that rectangle — there is
// no separately draggable label object.

import {
  alignmentToJustify,
  resolveMappingLabelText,
  type MappingPresentation,
} from '@helios/data-access';

export function MappedFieldBox({
  presentation,
  fieldLabel,
  children,
}: {
  presentation: MappingPresentation;
  /** Canonical field label, used only until the mapping has its own text. */
  fieldLabel: string | null | undefined;
  /** The mapped value — text, a signature preview, a placeholder. */
  children: React.ReactNode;
}) {
  const justify = alignmentToJustify(presentation.alignment);
  const labelText = resolveMappingLabelText(presentation, fieldLabel);
  const showLabel = presentation.showLabel && labelText !== '';

  // The value's own box. Alignment applies HERE, never to the outer frame, so
  // turning a label on cannot shift the value's box within the rectangle.
  const value = (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: justify,
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );

  if (!showLabel) {
    // Identical output to the pre-PRD.323 renderer, which is what keeps
    // existing mappings pixel-stable.
    return value;
  }

  const label = (
    <span
      style={{
        // Rendered exactly as authored — no colon added or stripped.
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontWeight: 600,
        opacity: 0.85,
        flexShrink: 0,
      }}
    >
      {labelText}
    </span>
  );

  // 'left' puts the label beside the value; 'above' stacks it. The label is
  // deliberately NOT given the value's alignment — V1 does not make label
  // alignment configurable, and inheriting it would push a left label to the
  // right edge whenever the value was right-aligned.
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: presentation.labelPosition === 'left' ? 'row' : 'column',
        alignItems: presentation.labelPosition === 'left' ? 'center' : 'stretch',
        justifyContent: 'center',
        gap: presentation.labelPosition === 'left' ? 6 : 1,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      {label}
      {value}
    </div>
  );
}
